import React from 'react';
import { motion } from 'framer-motion';
import { FaDownload, FaGraduationCap, FaBriefcase, FaCode, FaUsers } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SectionHeader from '../components/SectionHeader';
import resumeData from '../data/resume.json';
import '../styles/Resume.css';

// Type definitions for the imported JSON data
interface ResumeEducation {
  institution: string;
  location: string;
  degree: string;
  minor: string;
  startDate: string;
  endDate: string;
  specializations: string[];
  gpa: string;
  relevantCourses: string[];
}

interface SkillCategory {
  title: string;
  items: string[];
}

interface ResumeSkills {
  [key: string]: SkillCategory;
}

interface ResumeExperience {
  company: string;
  title: string;
  location: string;
  period: string;
  description: string[];
}

interface ResumeLeadership {
  title: string;
  event: string;
  organization: string;
  period: string;
  description: string[];
}

interface ResumeData {
  education: ResumeEducation;
  skills: ResumeSkills;
  workExperience: ResumeExperience[];
  leadershipAndActivities: ResumeLeadership[];
}

const Resume: React.FC = () => {
  // Cast imported JSON to typed data
  const data = resumeData as ResumeData;

  // Transform education data to display format
  const education = [{
    degree: data.education.degree,
    institution: data.education.institution,
    period: `Expected Graduation: ${data.education.endDate}`,
    gpa: data.education.gpa,
    details: [
      `Concentrations in ${data.education.specializations.join(' and ')}`,
      `Minor in ${data.education.minor}`,
      `Relevant Coursework: ${data.education.relevantCourses.join(', ')}`,
    ],
  }];

  // Transform skills data dynamically from JSON
  const skills: Record<string, string[]> = {};
  for (const key of Object.keys(data.skills)) {
    const category = data.skills[key];
    skills[category.title] = category.items;
  }

  // Transform work experience data
  const experience = data.workExperience.map(exp => ({
    title: exp.title,
    company: exp.company,
    location: exp.location,
    period: exp.period,
    description: exp.description,
  }));

  // Transform leadership data
  const leadership = data.leadershipAndActivities.map(item => ({
    title: item.title,
    company: item.event || item.organization,
    location: item.organization ? '—' : '—',
    period: item.period,
    description: item.description,
  }));

  return (
    <div className="resume-page">
      <Navbar />

      <section className="resume-header-section">
        <div className="container">
          <motion.div
            className="resume-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="resume-header-content">
              <div>
                <h1 className="resume-title">Resume</h1>
                <p className="resume-subtitle">
                  Full Stack Developer & Software Engineer
                </p>
              </div>
              <a
                href={`${process.env.PUBLIC_URL}/assets/resume.pdf`}
                download="Dawson_Pent_Resume.pdf"
                className="download-btn"
              >
                <FaDownload /> Download PDF
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="resume-content-section">
        <div className="container">
          {/* Education Section */}
          <motion.div
            className="resume-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="section-icon">
              <FaGraduationCap />
            </div>
            <SectionHeader title="Education" subtitle="Academic background and achievements" />
            <div className="education-list">
              {education.map((edu, index) => (
                <motion.div
                  key={index}
                  className="education-item"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="education-header">
                    <div>
                      <h3 className="education-degree">{edu.degree}</h3>
                      <p className="education-institution">{edu.institution}</p>
                    </div>
                    <div className="education-meta">
                      {edu.gpa && <span className="education-gpa">GPA: {edu.gpa}</span>}
                      <span className="education-period">{edu.period}</span>
                    </div>
                  </div>
                  <ul className="education-details">
                    {edu.details.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Skills Section */}
          <motion.div
            className="resume-section"
          >
            <div className="section-icon">
              <FaCode />
            </div>
            <motion.div
              className={`section-header`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="section-title">{"Skills"}</h2>
              <p className="section-subtitle">{"Technical proficiencies and expertise"}</p>
              <div className="section-divider"></div>
            </motion.div>
            <div className="skills-grid">
              {Object.entries(skills).map(([category, items]) => (
                <motion.div
                  key={category}
                  className="skill-category"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h3 className="skill-category-title">{category}</h3>
                  <div className="skill-items">
                    {items.map((skill) => (
                      <span key={skill} className="skill-item">
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Experience Section */}
          <motion.div
            className="resume-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-icon">
              <FaBriefcase />
            </div>
            <SectionHeader title="Experience" subtitle="Professional and project experience" />
            <div className="experience-timeline">
              {experience.map((exp, index) => (
                <motion.div
                  key={index}
                  className="experience-item"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                >
                  <div className="experience-header">
                    <div>
                      <h3 className="experience-title">{exp.title}</h3>
                      <p className="experience-company">{exp.company}</p>
                    </div>
                    <span className="experience-period">{exp.period}</span>
                  </div>
                  <ul className="experience-description">
                    {exp.description.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Leadership & Activities Section */}
          <motion.div
            className="resume-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-icon">
              <FaUsers />
            </div>

            <SectionHeader
              title="Leadership & Activities"
              subtitle="Competitive, academic, and community leadership roles"
            />

            <div className="experience-timeline">
              {leadership.map((item, index) => (
                <motion.div
                  key={index}
                  className="experience-item"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                >
                  <div className="experience-header">
                    <div>
                      <h3 className="experience-title">{item.title}</h3>
                      <p className="experience-company">{item.company}</p>
                    </div>
                    <span className="experience-period">{item.period}</span>
                  </div>

                  <ul className="experience-description">
                    {item.description.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resume;
